import { yupResolver } from '@hookform/resolvers/yup';
import React, { useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { AccountDistributionOrderModal } from '../../../AccountDistributionOrderModal/AccountDistributionOrderModal';
import {
  AccountDistributionOrderDTO,
  AccountDistributionStrategyIdDTO,
} from '../../../AccountDistributionOrderModal/api/useAccountDistributionOptionsQuery';
import { BASE_ACCOUNT_DISTRIBUTION_STRATEGY_OPTIONS } from '../../../AdviserSettingsOPL/tabs/FirmSettings/components/Assumptions/Assumptions.meta';
import { CurrentYearAssumptionSetting } from '../../../CommonComponents/CurrentYearAssumptionSetting/CurrentYearAssumptionSetting';
import { Box } from '../../../CommonComponents/OPL/Box/Box';
import { Button } from '../../../CommonComponents/OPL/Button/Button';
import { Checkbox } from '../../../CommonComponents/OPL/Checkbox/Checkbox';
import { FullPageLoading } from '../../../CommonComponents/OPL/FullpageLoading/FullPageLoading';
import { HelperText } from '../../../CommonComponents/OPL/HelperText/HelperText';
import { Input } from '../../../CommonComponents/OPL/Input/Input';
import { InputNumberMask } from '../../../CommonComponents/OPL/Input/InputNumberMask';
import { Select } from '../../../CommonComponents/OPL/Select/Select/Select';
import { toast } from '../../../CommonComponents/OPL/Toast/Toast';
import { MESSAGES } from '../../../CommonConstants';
import {
  selectAssetBucketingHeadline,
  selectIsCanadian,
} from '../../../CommonSelectors/CommonAreaSelector';
import { ManageExcessCashModal } from '../../../ManageExcessCashModal/ManageExcessCashModal';
import { ManualAccountsAssetAllocationStrategyIdDTO } from '../../../ManualAccountsAssetAllocation/types';
import { ManualAccountsAssetAllocationModal } from '../../../ManualAccountsAssetAllocationModal/ManualAccountsAssetAllocationModal';
import { useClientSettingsMutation } from '../../api/useClientSettingsMutation';
import { PlanStartYearDTO } from '../../api/useClientSettingsQuery';
import { AsyncData } from '../../useClientSettings';
import { FormValues, TOAST_SUCCESS_MESSAGE, Validation } from './ClientSettingsForm.meta';
import { Integrated } from './Integrated';
import { useClientId } from '../../../_hooks/useClientId';

const CLIENT_ACCOUNT_DISTRIBUTION_STRATEGY_OPTIONS = [
  ...BASE_ACCOUNT_DISTRIBUTION_STRATEGY_OPTIONS,
  { label: 'Use Adviser Settings', value: AccountDistributionStrategyIdDTO.Inherit },
];

interface Props {
  data: AsyncData;
  accountDistributionOrder: AccountDistributionOrderDTO;
}

export const ClientSettingsForm: React.FC<Props> = ({ data, accountDistributionOrder }) => {
  const [showCurrentYearAssumptionSetting, setCurrentYearAssumptionSettingVisibility] = useState(
    false,
  );

  const [isAccountDistributionOrderModalOpen, setIsAccountDistributionOrderModalOpen] = useState(
    false,
  );

  const [isManageExcessCashModalOpen, setIsManageExcessCashModalOpen] = useState(false);
  const [isDefaultAssetAllocationModalOpen, setIsDefaultAssetAllocationModalOpen] = useState(false);

  const assetBucketingHeadline = useSelector(selectAssetBucketingHeadline);

  const clientId = useClientId();
  const isCanadian = useSelector(selectIsCanadian);

  const { options, settings } = data;

  const formBag = useForm<FormValues>({
    resolver: yupResolver(Validation),
    defaultValues: {
      Inflation: settings.Inflation,
      LifeExpectancyPrincipal: settings.LifeExpectancyPrincipal,
      PortfolioReturnsMethodology: settings.PortfolioReturnsMethodology,
      SocialSecurityInflation: settings.SocialSecurityInflation,
      LifeExpectancySpouse: settings.LifeExpectancySpouse,
      EmployerId: settings.EmployerId,
      EducationInflation: settings.EducationInflation,
      AllowAssetBucketing: settings.AllowAssetBucketing,
      LoanBalanceInterest: settings.LoanBalanceInterest,
      CashflowEngine: settings.CashflowEngine,
      SocialSecurityUseSpousalBenefit: settings.SocialSecurityUseSpousalBenefit,
      SocialSecurityFileForSurvivorBenefits: settings.SocialSecurityFileForSurvivorBenefits,
      AllowBeFi20: settings.AllowBeFi20,
      RiskToleranceWorkflow: settings.RiskToleranceWorkflow,
      CapitalMarketAssumptions: settings.CapitalMarketAssumptions,
      AllowPulseCheck: settings.AllowPulseCheck,
      PlanStartYear: settings.PlanStartYear,
      ExcessCashFlowPercentToSave: settings.ExcessCashFlowPercentToSave,
      ManualAccountsAssetAllocation: settings.ManualAccountsAssetAllocation,
      MonteCarloSuccessRange: settings.MonteCarloSuccessRange,
      AccountDistributionStrategy: settings.AccountDistributionStrategy,
      AccountDistributionOrder: settings.AccountDistributionOrder ?? {
        preRetirement: accountDistributionOrder.default,
        postRetirement: null,
      },
      ImposeNetInvestmentIncomeTax: settings.ImposeNetInvestmentIncomeTax,
    },
  });

  const { watch, setValue, formState } = formBag;

  const planStartYear = watch('PlanStartYear');
  const accountDistributionStrategy = watch('AccountDistributionStrategy');
  const currentAccountDistributionOrder = watch('AccountDistributionOrder');
  const excessCashFlowPercentToSave = watch('ExcessCashFlowPercentToSave');
  const manualAccountsAssetAllocation = watch('ManualAccountsAssetAllocation');

  const {
    mutateAsync: updateClientSettings,
    isLoading: isUpdatingClientSettings,
  } = useClientSettingsMutation();

  const onSubmit = async (values: FormValues) => {
    try {
      await updateClientSettings(values);

      toast.success(TOAST_SUCCESS_MESSAGE);
    } catch (err) {
      console.error(err);

      if (err instanceof Error) {
        toast.error(err.message);
      } else {
        toast.error(MESSAGES.GENERIC_ERROR);
      }
    }
  };

  return (
    <>
      <FormProvider {...formBag}>
        {isUpdatingClientSettings && <FullPageLoading />}

        <form
          onSubmit={formBag.handleSubmit(onSubmit)}
          css={`
            display: flex;
            flex-direction: column;
            height: 100%;
          `}
        >
          <Box
            display="grid"
            css={`
              grid-template-columns: repeat(3, 1fr);
              row-gap: 32px;
              column-gap: 48px;
            `}
          >
            <Input
              rightIcon="far fa-percent"
              label="Inflation Rate"
              {...formBag.register('Inflation')}
            />

            {settings.PrincipalName && (
              <Input
                innerText="years"
                label={`${settings.PrincipalName} Life Expectancy`}
                {...formBag.register('LifeExpectancyPrincipal')}
              />
            )}

            {settings.AdvisersAssumptionsEnabled && (
              <Controller
                control={formBag.control}
                name="PortfolioReturnsMethodology"
                render={({ field: { value } }) => (
                  <Select
                    value={options.portfolioReturnsOptions.find(
                      ({ value: _value }) => value === _value,
                    )}
                    options={options.portfolioReturnsOptions}
                    onChange={opt => {
                      if (opt) {
                        formBag.setValue('PortfolioReturnsMethodology', opt.value);
                      }
                    }}
                    label="Portfolio Returns"
                  />
                )}
              />
            )}

            {!isCanadian && (
              <Input
                rightIcon="far fa-percent"
                label="Social Security Inflation Rate"
                {...formBag.register('SocialSecurityInflation')}
              />
            )}

            {settings.SpouseName && (
              <Input
                innerText="years"
                label={`${settings.SpouseName} Life Expectancy`}
                {...formBag.register('LifeExpectancySpouse')}
              />
            )}

            <Input
              rightIcon="far fa-percent"
              label="Tuition Inflation Rate"
              {...formBag.register('EducationInflation')}
            />

            <Box display="flex" flexDirection="column" alignItems="flex-start">
              <Box alignSelf="stretch">
                <Controller
                  control={formBag.control}
                  name="ExcessCashFlowPercentToSave"
                  render={({ field: { value } }) => (
                    <InputNumberMask
                      value={value}
                      label="Excess Cash Flow Savings Rate"
                      allowNegative={false}
                      decimalScale={2}
                      rightIcon="far fa-percent"
                      onValueChange={({ floatValue }) => {
                        formBag.setValue(
                          'ExcessCashFlowPercentToSave',
                          floatValue === undefined ? null : floatValue,
                          {
                            shouldValidate: true,
                            shouldDirty: true,
                          },
                        );
                      }}
                      tooltip="Enter a value between 0-100, where 0% = Spend, 1-100% = Save"
                      helperText={
                        formBag.formState.errors.ExcessCashFlowPercentToSave?.message || ''
                      }
                      formVariant={
                        formBag.formState.errors.ExcessCashFlowPercentToSave ? 'error' : undefined
                      }
                    />
                  )}
                />
              </Box>
              {excessCashFlowPercentToSave !== null && excessCashFlowPercentToSave > 0 && (
                <Button
                  type="button"
                  variant="link"
                  leftIcon="far fa-pencil"
                  mt="12"
                  onClick={() => setIsManageExcessCashModalOpen(true)}
                >
                  Manage Saved Cash Allocation
                </Button>
              )}
            </Box>

            {settings.DisplayAssetBucketing && (
              <Controller
                control={formBag.control}
                name="AllowAssetBucketing"
                render={({ field: { value } }) => (
                  <Select
                    value={options.assetBucketingOptions.find(
                      ({ value: _value }) => value === _value,
                    )}
                    options={options.assetBucketingOptions}
                    onChange={opt => {
                      if (opt) {
                        formBag.setValue('AllowAssetBucketing', opt.value);
                      }
                    }}
                    label={`Asset Bucketing - ${assetBucketingHeadline}`}
                  />
                )}
              />
            )}

            {settings.DisplayBeFi20 && (
              <Controller
                control={formBag.control}
                name="AllowBeFi20"
                render={({ field: { value } }) => (
                  <Select
                    value={options.beFi20Options.find(({ value: _value }) => value === _value)}
                    options={options.beFi20Options}
                    onChange={opt => {
                      if (opt) {
                        formBag.setValue('AllowBeFi20', opt.value);
                      }
                    }}
                    label="BeFi20"
                  />
                )}
              />
            )}

            {settings.DisplayPulseCheck && (
              <Controller
                control={formBag.control}
                name="AllowPulseCheck"
                render={({ field: { value } }) => (
                  <Select
                    value={options.pulseCheckOptions.find(({ value: _value }) => value === _value)}
                    options={options.pulseCheckOptions}
                    onChange={opt => {
                      if (opt) {
                        formBag.setValue('AllowPulseCheck', opt.value);
                      }
                    }}
                    label="PulseCheck"
                  />
                )}
              />
            )}

            <Input
              rightIcon="far fa-percent"
              label="Loan Balance Interest Rate"
              {...formBag.register('LoanBalanceInterest')}
            />

            {settings.ShowCashflowEngineVersionSelector && (
              <Controller
                control={formBag.control}
                name="CashflowEngine"
                render={({ field: { value } }) => (
                  <Select
                    value={options.cashflowEngines.find(({ value: _value }) => value === _value)}
                    options={options.cashflowEngines}
                    onChange={opt => {
                      if (opt) {
                        formBag.setValue('CashflowEngine', opt.value);
                      }
                    }}
                    label="Cash Flow Simulation"
                  />
                )}
              />
            )}

            {settings.Is3dRiskToleranceWorkflowEnabled && (
              <Controller
                control={formBag.control}
                name="RiskToleranceWorkflow"
                render={({ field: { value, onChange } }) => (
                  <Select
                    label="Risk Questionnaire"
                    value={options.riskToleranceWorkflowOptions.find(
                      ({ value: _value }) => value === _value,
                    )}
                    options={options.riskToleranceWorkflowOptions}
                    onChange={opt => onChange(opt?.value)}
                  />
                )}
              />
            )}

            <Controller
              control={formBag.control}
              name="CapitalMarketAssumptions"
              render={({ field: { value, onChange } }) => (
                <Select
                  label="Capital Market Assumptions"
                  value={options.capitalMarketAssumptionsOptions.find(
                    ({ value: _value }) => value === _value,
                  )}
                  options={options.capitalMarketAssumptionsOptions}
                  onChange={opt => onChange(opt?.value)}
                />
              )}
            />

            <Box display="flex" flexDirection="column" alignItems="flex-start">
              <Box alignSelf="stretch">
                <Controller
                  control={formBag.control}
                  name="PlanStartYear"
                  render={({ field: { value, onChange } }) => (
                    <Select
                      label="Plan Start Year"
                      value={options.planStartYearOptions.find(
                        ({ value: _value }) => value === _value,
                      )}
                      options={options.planStartYearOptions}
                      onChange={opt => onChange(opt?.value)}
                    />
                  )}
                />
              </Box>

              {planStartYear === PlanStartYearDTO.CurrentYear && (
                <Button
                  type="button"
                  variant="link"
                  leftIcon="far fa-pencil"
                  onClick={() => {
                    setCurrentYearAssumptionSettingVisibility(true);
                  }}
                  mt="12"
                >
                  Manage Current Year Assumptions
                </Button>
              )}
            </Box>

            <Box>
              <Controller
                control={formBag.control}
                name="AccountDistributionStrategy"
                render={({ field: { value } }) => (
                  <Select
                    value={CLIENT_ACCOUNT_DISTRIBUTION_STRATEGY_OPTIONS.find(
                      ({ value: _value }) => value === _value,
                    )}
                    options={CLIENT_ACCOUNT_DISTRIBUTION_STRATEGY_OPTIONS}
                    onChange={opt => {
                      if (opt) {
                        formBag.setValue('AccountDistributionStrategy', opt.value);
                      }
                    }}
                    label="Account Distribution Strategy"
                    tooltip="Customize the order in which accounts are distributed. This order is conceptual—actual plan behavior depends on account type, how each account is earmarked to goals, and the exhaustion or creation of accounts in future years."
                  />
                )}
              />

              {accountDistributionStrategy === AccountDistributionStrategyIdDTO.Custom && (
                <Button
                  mt="8"
                  type="button"
                  variant="link"
                  leftIcon="far fa-pencil"
                  onClick={() => setIsAccountDistributionOrderModalOpen(true)}
                >
                  Edit Distribution Order
                </Button>
              )}

              <AccountDistributionOrderModal
                isOpen={isAccountDistributionOrderModalOpen}
                onClose={() => setIsAccountDistributionOrderModalOpen(false)}
                options={accountDistributionOrder.options}
                defaultOrder={accountDistributionOrder.default}
                order={currentAccountDistributionOrder}
                onOrderChange={order => formBag.setValue('AccountDistributionOrder', order)}
              />
            </Box>

            <Box display="flex" flexDirection="column" alignItems="flex-start">
              <Box alignSelf="stretch">
                <Controller
                  control={formBag.control}
                  name="ManualAccountsAssetAllocation.strategy"
                  render={({ field: { value, onChange } }) => (
                    <>
                      <Select
                        label="Default Asset Allocation for Manual Accounts"
                        value={options.defaultAssetAllocationOptions.find(
                          ({ value: _value }) => value === _value,
                        )}
                        options={options.defaultAssetAllocationOptions}
                        onChange={opt => {
                          if (opt !== null && opt?.value !== null) {
                            onChange(opt.value);

                            setValue('ManualAccountsAssetAllocation', {
                              strategy: opt.value,
                              details: null,
                            });
                          }
                        }}
                        tooltip="Set a default asset allocation when you add accounts to the current plan. In the proposed plan, the mix of investments changes based on the recommended allocation for each goal."
                      />
                      {formState.errors.ManualAccountsAssetAllocation?.details &&
                        'message' in formState.errors.ManualAccountsAssetAllocation?.details && (
                          <HelperText variant="error" mt="4">
                            {formState.errors.ManualAccountsAssetAllocation?.details?.message}
                          </HelperText>
                        )}
                    </>
                  )}
                />
              </Box>

              {[
                ManualAccountsAssetAllocationStrategyIdDTO.CMA,
                ManualAccountsAssetAllocationStrategyIdDTO.Model,
              ].includes(manualAccountsAssetAllocation.strategy) && (
                <Button
                  type="button"
                  variant="link"
                  leftIcon="far fa-pencil"
                  onClick={() => {
                    setIsDefaultAssetAllocationModalOpen(true);
                  }}
                  mt="12"
                >
                  Manage Default Asset Allocation
                </Button>
              )}

              {isDefaultAssetAllocationModalOpen && (
                <ManualAccountsAssetAllocationModal
                  key={manualAccountsAssetAllocation.strategy}
                  isOpen={isDefaultAssetAllocationModalOpen}
                  onClose={() => setIsDefaultAssetAllocationModalOpen(false)}
                  allocation={manualAccountsAssetAllocation}
                  onChange={allocation => setValue('ManualAccountsAssetAllocation', allocation)}
                  clientId={clientId}
                />
              )}
            </Box>
            <Controller
              control={formBag.control}
              name="MonteCarloSuccessRange"
              render={({ field: { value, onChange } }) => (
                <InputNumberMask
                  value={value}
                  label="Monte Carlo Success Range"
                  placeholder="Use Advisor Settings"
                  allowNegative={false}
                  decimalScale={0}
                  rightIcon="far fa-percent"
                  onValueChange={({ floatValue }) => {
                    formBag.setValue(
                      'MonteCarloSuccessRange',
                      floatValue === undefined ? null : floatValue,
                      {
                        shouldValidate: true,
                        shouldDirty: true,
                      },
                    );
                  }}
                  tooltip="The default success range is 70 - 99%"
                  helperText={formBag.formState.errors.MonteCarloSuccessRange?.message || ''}
                  formVariant={
                    formBag.formState.errors.MonteCarloSuccessRange ? 'error' : undefined
                  }
                />
              )}
            />

            <Controller
              control={formBag.control}
              name="ImposeNetInvestmentIncomeTax"
              render={({ field: { value, onChange } }) => (
                <Select
                  label="Impose Net Investment Income Tax"
                  value={options.netInvestmentIncomeTaxOptions.find(
                    ({ value: _value }) => value === _value,
                  )}
                  options={options.netInvestmentIncomeTaxOptions}
                  onChange={opt => onChange(opt?.value)}
                />
              )}
            />
          </Box>

          {settings.SpouseName && !isCanadian && (
            <>
              <Checkbox mt="32" {...formBag.register('SocialSecurityUseSpousalBenefit')}>
                File for Social Security Spousal Benefit
              </Checkbox>

              <Checkbox mt="32" {...formBag.register('SocialSecurityFileForSurvivorBenefits')}>
                File for Social Security Survivor Benefit
              </Checkbox>
            </>
          )}

          <Integrated integratedSystems={data.settings.IntegratedSystems} />

          <Box
            width="100%"
            display="flex"
            justifyContent="flex-end"
            borderTop="1px solid"
            borderColor="neutral.200"
            mt="auto"
            mb={-32}
          >
            <Button my="24" variant="subtleInactive" onClick={() => formBag.reset()}>
              Revert
            </Button>

            <Button type="submit" my="24" ml="24">
              Save
            </Button>
          </Box>
        </form>
      </FormProvider>

      {showCurrentYearAssumptionSetting && (
        <CurrentYearAssumptionSetting
          onClose={() => {
            setCurrentYearAssumptionSettingVisibility(false);
          }}
        />
      )}
      {isManageExcessCashModalOpen && (
        <ManageExcessCashModal
          showGoalEarmark
          onClose={() => {
            setIsManageExcessCashModalOpen(false);
          }}
        />
      )}
    </>
  );
};
